import { Edit, Trash2, UserPlus, ChevronLeft, ChevronRight, Search, Eye, X, Mail, ArrowRightLeft, LockOpen, Users } from "lucide-react";
import AgentCompareDialog from "./AgentCompareDialog";
import { useState, useMemo, useEffect, useRef } from "react";

import { Avatar, AvatarImage, AvatarFallback } from "@/app/_module/components/ui/avatar";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";
import { RemoveAgentDialog, type RemoveAgentMode } from "./RemoveAgentDialog";
import { verifyMFAForAction, resendSMSForAction } from "@/app/_module/services/auth";
import { useToast } from "@/app/_module/hooks/use-toast";

import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useDisableEligibility, useRequestDeactivation, useSuspendAgent, useUnsuspendAgent, useRestoreAgent, useDeleteAgencyUser, useGetAgencyUsers, useCancelInvite } from "../../hooks/use-hook";
import type { AgencyUserWithAgency, PendingInvite } from "../../types/types";

import { InviteUsersModal } from "./InviteUsersModal";
import EditUserRoleModal from "./EditUserRole";
import { MoveAgentModal } from "./MoveAgentModal";
import { BulkMoveAgentsModal } from "./BulkMoveAgentsModal";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface AgencyUsersTableProps {
  agencyId: string;
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void;
  /** Date range for Compare — the same params the Agent Details view gets. */
  period?: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
  // When true, lists agents across this agency + all sub-agencies in one table
  // and shows an "Agency" column so you don't have to drill into each.
  includeSubAgencies?: boolean;
}

/**
 * Suspended vs disabled vs fine.
 *
 * Both locked states land on `accountStatus: 'suspended'` — the DB trigger
 * early-returns on that value, so it is the one that reliably sticks and we don't
 * invent a new status to fight it. `deactivationReason` is set ONLY by disable, so
 * that is the discriminator. `deletedAt` means the owner also opted into the 30-day
 * deletion clock, which is the only case with a deadline to show.
 */
type LockState = { kind: "active" | "suspended" | "disabled"; daysLeft: number | null };
function lockStateOf(u: any): LockState {
  const locked = u?.isActive === false || String(u?.accountStatus ?? "").toLowerCase() === "suspended";
  if (!locked) return { kind: "active", daysLeft: null };
  const kind = u?.deactivationReason ? "disabled" : "suspended";
  let daysLeft: number | null = null;
  if (u?.deletedAt) {
    const due = new Date(u.deletedAt).getTime() + 30 * 24 * 60 * 60 * 1000;
    daysLeft = Math.max(0, Math.ceil((due - Date.now()) / (24 * 60 * 60 * 1000)));
  }
  return { kind, daysLeft };
}

export const AgencyUsersTable = function({ agencyId, onViewAgentDetails, includeSubAgencies, period = "6-months", startDate, endDate }: AgencyUsersTableProps) {
  const [compareOpen, setCompareOpen] = useState(false);
    // Persist page + page-size so opening an agent's detail view (which
    // unmounts this table) and coming back returns you to the same page.
    const PAGE_KEY = `agencyAgentsPage:${agencyId}`;
    const PER_PAGE_KEY = 'agencyAgentsPerPage';
    const [currentPage, setCurrentPage] = useState<number>(() => {
      if (typeof window === 'undefined') return 1;
      const saved = parseInt(sessionStorage.getItem(PAGE_KEY) || '', 10);
      return saved > 0 ? saved : 1;
    });
    const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
      // Downline view spans many sub/sub-sub agencies — default to 50/page so
      // the whole org is visible at once instead of hiding sub-agencies on
      // later pages. Single-agency view keeps the compact 10 default.
      const fallback = includeSubAgencies ? 50 : 10;
      if (typeof window === 'undefined') return fallback;
      const saved = parseInt(sessionStorage.getItem(PER_PAGE_KEY) || '', 10);
      return [10, 25, 50, 100].includes(saved) ? saved : fallback;
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [userToDelete, setUserToDelete] = useState<AgencyUserWithAgency | null>(null);
    const [showMFAModal, setShowMFAModal] = useState(false);
    const [showRemoveChoice, setShowRemoveChoice] = useState(false);
    const [removeMode, setRemoveMode] = useState<RemoveAgentMode>("remove");
    const [scheduleDeletion, setScheduleDeletion] = useState(false);
    const [autoDisableAfterDays, setAutoDisableAfterDays] = useState<number | null>(null);

    const [inviteToCancel, setInviteToCancel] = useState<PendingInvite | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [showBulkMove, setShowBulkMove] = useState(false);
    const { toast } = useToast();
    const { user } = useUserContext();
    const currentUserId = (user as any)?.user?.id || (user as any)?.id;

  // Helper function to get account type display info
  // Soft-pill badge system: borderless tinted pills with an inset ring —
  // one visual language for role/account/A2P/status instead of mixed outlines.
  const PILL_BASE = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset whitespace-nowrap';
  const PILL_TONES: Record<string, string> = {
    red: 'bg-red-50 text-red-700 ring-red-600/20',
    violet: 'bg-violet-50 text-violet-700 ring-violet-600/20',
    blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    amber: 'bg-amber-50 text-amber-800 ring-amber-600/25',
    rose: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    gray: 'bg-gray-50 text-gray-600 ring-gray-500/20',
    slate: 'bg-slate-100 text-slate-700 ring-slate-500/20',
    primary: 'bg-primary/5 text-primary ring-primary/25',
    green: 'bg-green-50 text-green-700 ring-green-600/20',
    yellow: 'bg-yellow-50 text-yellow-800 ring-yellow-600/25',
  };
  const pill = (tone: keyof typeof PILL_TONES) => `${PILL_BASE} ${PILL_TONES[tone] || PILL_TONES.gray}`;

  const getAccountTypeInfo = (accountType: string) => {
    switch (accountType.toLowerCase()) {
      case 'full_fledged':
        return { label: 'Full-Fledged', tone: 'primary' as const };
      case 'super_agent':
        return { label: 'Super Agent', tone: 'violet' as const };
      case 'enterprise':
        return { label: 'Enterprise', tone: 'slate' as const };
      case 'basic':
        return { label: 'Basic', tone: 'gray' as const };
      case 'trial':
        return { label: 'Trial', tone: 'amber' as const };
      default:
        return { label: accountType, tone: 'gray' as const };
    }
  };

  // Helper function to format role display
  const formatRole = (role: string) => {
    // Convert role to readable format
    const roleMap: Record<string, string> = {
      'owner': 'Owner',
      'admin': 'Agency Admin',
      'manager': 'Agency Admin',
      'agent': 'Agent',
      'member': 'Member',
    };
    return roleMap[role.toLowerCase()] || role;
  };

  const { data: agencyUsersResponse, isLoading, error } = useGetAgencyUsers(agencyId, includeSubAgencies);
  const deleteAgencyUserMutation = useDeleteAgencyUser();
  const cancelInviteMutation = useCancelInvite(agencyId ?? undefined);

  const agencyUsers = useMemo(() => {
    // A membership row whose user record is gone (account soft-deleted while the
    // membership stayed live) comes back from the API as `user: null`. It is not a
    // person and must never reach render/search — one such row crashed the whole
    // tab the moment anyone typed (Moe Ray, 2026-09-14).
    const withUser = (rows: any[]) => rows.filter((r) => r && r.user);

    // Handle different response structures
    if (!agencyUsersResponse) return [];

    // If response has a 'data' property, use that
    if ('data' in agencyUsersResponse && Array.isArray(agencyUsersResponse.data)) {
      return withUser(agencyUsersResponse.data);
    }

    // If response has a 'users' property (fallback from 404 handler), use that
    if ('users' in agencyUsersResponse && Array.isArray(agencyUsersResponse.users)) {
      return withUser(agencyUsersResponse.users);
    }

    // If response is directly an array
    if (Array.isArray(agencyUsersResponse)) {
      return withUser(agencyUsersResponse);
    }

    return [];
  }, [agencyUsersResponse]);

  // Extract pending invites from response
  const pendingInvites = useMemo(() => {
    if (!agencyUsersResponse) return [];

    if ('pendingInvites' in agencyUsersResponse && Array.isArray(agencyUsersResponse.pendingInvites)) {
      return agencyUsersResponse.pendingInvites;
    }

    return [];
  }, [agencyUsersResponse]);

  // Add search filtering logic
  const filteredUsers = useMemo(() => {
    const matched = !searchTerm.trim()
      ? agencyUsers
      : agencyUsers.filter(user => {
          const q = searchTerm.toLowerCase();
          const has = (v: unknown) => typeof v === 'string' && v.toLowerCase().includes(q);
          return (
            has(user.user?.name) ||
            has(user.user?.email) ||
            has(user.role) ||
            has(user.user?.title) ||
            has(user.user?.accountType) ||
            has((user as any).upline?.map((u: any) => u?.name).join(' '))
          );
        });

    // Downline view: group by AGENCY (then name) so every sub/sub-sub agency's
    // team sits together and no agency looks missing. The API orders by agency
    // UUID (effectively random), which scattered sub-agencies across pages and
    // read as "downline not showing". Single-agency view keeps API order.
    if (!includeSubAgencies) return matched;
    return [...matched].sort((a, b) => {
      const agA = (a as any).agency?.name || '';
      const agB = (b as any).agency?.name || '';
      if (agA !== agB) return agA.localeCompare(agB);
      return (a.user?.name || '').localeCompare(b.user?.name || '');
    });
  }, [agencyUsers, searchTerm, includeSubAgencies]);

  // Filter pending invites based on search term
  const filteredPendingInvites = useMemo(() => {
    if (!searchTerm.trim()) return pendingInvites;

    const q = searchTerm.toLowerCase();
    const has = (v: unknown) => typeof v === 'string' && v.toLowerCase().includes(q);
    return pendingInvites.filter(invite =>
      has(invite.email) || has(invite.role) || 'pending'.includes(q)
    );
  }, [pendingInvites, searchTerm]);

  // Frontend pagination logic
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]) || [];

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Reset to first page when the search term actually CHANGES — but skip the
  // first run (mount), otherwise a page restored from sessionStorage would be
  // clobbered back to 1 every time you return from an agent's detail view.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    setCurrentPage(1);
  }, [searchTerm]);

  // Persist page + page-size across the unmount.
  useEffect(() => {
    try { sessionStorage.setItem(PAGE_KEY, String(currentPage)); } catch {}
  }, [currentPage, PAGE_KEY]);
  useEffect(() => {
    try { sessionStorage.setItem(PER_PAGE_KEY, String(itemsPerPage)); } catch {}
  }, [itemsPerPage]);

  // If the list shrank below the restored page, clamp to the last valid page.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteUser = (user: AgencyUserWithAgency) => {
    // DEMO SANDBOX: removing an agent deletes agency membership for good — the seeded
    // downline is what makes leaderboards, production reports and the agency tree look
    // real, so a visitor pruning it would hand the NEXT visitor a hollowed-out agency.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Removing agents is disabled in the demo sandbox. Start a free trial to manage your real team.",
        }),
      );
      return;
    }

    setUserToDelete(user);
    setShowRemoveChoice(true); // pick remove vs disable first, then 2FA
  };

  const requestDeactivationMutation = useRequestDeactivation();
  const suspendMutation = useSuspendAgent();
  const unsuspendMutation = useUnsuspendAgent();
  const restoreMutation = useRestoreAgent();

  /** Lift a suspension, or undo a disable. Both refresh the roster so the row recolours. */
  const handleUnlock = async (agencyUser: any) => {
    const st = lockStateOf(agencyUser.user);
    const args = { agencyId: agencyUser.agency?.id || agencyUser.agencyId || agencyId, userId: agencyUser.userId };
    try {
      if (st.kind === "disabled") {
        const res: any = await restoreMutation.mutateAsync(args);
        toast({
          title: "Account restored",
          description: res?.message || `${agencyUser.user.name} can sign in again.`,
        });
      } else {
        await unsuspendMutation.mutateAsync(args);
        toast({ title: "Suspension lifted", description: `${agencyUser.user.name} can sign in again.` });
      }
    } catch (error: any) {
      toast({ title: "Couldn't undo that", description: error?.message || "Please try again.", variant: "destructive" });
    }
  };
  const { data: eligibility } = useDisableEligibility(
    showRemoveChoice ? ((userToDelete as any)?.agency?.id || userToDelete?.agencyId || agencyId) : undefined,
    showRemoveChoice ? userToDelete?.userId : undefined,
  );

  const handleRemoveChoice = async (
    mode: RemoveAgentMode,
    opts?: { scheduleDeletion: boolean; autoDisableAfterDays: number | null },
  ) => {
    setShowRemoveChoice(false); // not via onOpenChange: that path clears userToDelete
    // Suspend locks someone out of their account, so it sits behind the same 2FA
    // step as Remove and Disable rather than firing straight off a menu click.
    if (mode === "suspend") {
      setAutoDisableAfterDays(opts?.autoDisableAfterDays ?? null);
      setRemoveMode("suspend");
      setShowMFAModal(true);
      return;
    }
    if (mode === "disable") setScheduleDeletion(!!opts?.scheduleDeletion);
    if (mode === "request") {
      if (!userToDelete) return;
      try {
        const res: any = await requestDeactivationMutation.mutateAsync({
          agencyId: (userToDelete as any)?.agency?.id || userToDelete?.agencyId || agencyId,
          userId: userToDelete.userId,
        });
        toast({ title: "Request sent", description: res?.message || "InsuraCentral will review the deactivation request." });
      } catch (error: any) {
        toast({ title: "Couldn't send request", description: error?.message || "Please try again.", variant: "destructive" });
      }
      setUserToDelete(null);
      return;
    }
    setRemoveMode(mode);
    setShowMFAModal(true);
  };

  const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
    try {
      const verified = await verifyMFAForAction(code, method);
      return verified;
    } catch (error) {
      return false;
    }
  };

  const handleMFAVerified = async () => {
    if (userToDelete && removeMode === "suspend") {
      const who = userToDelete;
      try {
        const res: any = await suspendMutation.mutateAsync({
          agencyId: (who as any).agency?.id || who.agencyId || agencyId,
          userId: who.userId,
          autoDisableAfterDays,
        });
        toast({
          title: "Agent suspended",
          description: res?.message || `${who.user.name} can no longer sign in. Everything else is untouched.`,
        });
      } catch (error: any) {
        toast({ title: "Couldn't suspend", description: error?.message || "Please try again.", variant: "destructive" });
      }
      setUserToDelete(null);
      setAutoDisableAfterDays(null);
      return;
    }
    if (userToDelete) {
      try {
        await deleteAgencyUserMutation.mutateAsync({
          // Remove from the AGENT'S OWN agency — with includeSubAgencies the list
          // mixes people from sub/sub-sub agencies, so using the viewer's agencyId
          // deleted nothing ("User association not found") for anyone below. The
          // agent row carries its real agency; fall back to the viewer's only if
          // absent.
          agencyId: (userToDelete as any).agency?.id || userToDelete.agencyId || agencyId,
          data: {
            userId: userToDelete.userId,
            role: userToDelete.role,
            disableAccount: removeMode === "disable",
            scheduleDeletion: removeMode === "disable" ? scheduleDeletion : false,
          },
        });
        setUserToDelete(null);
        toast({
          title: removeMode === "disable" ? "Account disabled" : "User removed",
          description:
            removeMode === "disable"
              ? `${userToDelete.user.name} has been signed out, their subscription and numbers cancelled, and they were removed from the agency.`
              : `${userToDelete.user.name} has been removed from the agency.`,
        });
      } catch (error: any) {
        // Surface the backend's actual reason (e.g. "This person is an agency
        // owner… contact support") instead of a generic line. The message is on
        // the transformed error thrown by the service.
        const message =
          error?.message ||
          error?.response?.data?.message ||
          "Failed to remove user from agency";
        toast({
          title: "Couldn't remove user",
          description: message,
          variant: "destructive",
        });
      }
    }
  };

  const handleCancelInvite = async (invite: PendingInvite) => {
    try {
      await cancelInviteMutation.mutateAsync(invite.id);
      toast({
        title: "Invite cancelled",
        description: `The invitation to ${invite.email} has been cancelled.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel invite",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="border-none rounded-none shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Agents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading agents...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-none rounded-none shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Agents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">Failed to load agents</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!agencyUsers || agencyUsers.length === 0) {
    return (
      <Card className="border-none rounded-none shadow-none">
        <div className="flex justify-between items-center mb-4">
          <CardTitle className="flex gap-2">
            Agents
          </CardTitle>
          <div data-tour="invite-agents" className="flex gap-2 justify-end">
            <InviteUsersModal
              agency={{ id: agencyId, name: "Current Agency", description: "", contactEmail: "", isActive: true, updatedAt: "", createdAt: "", deletedAt: null }}
              trigger={<Button size="sm" variant="outline">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Agent
              </Button>}
            />
          </div>
        </div>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">No agents found for this agency</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none rounded-none shadow-none">
      <AgentCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        agencyId={agencyId}
        period={period}
        startDate={startDate}
        endDate={endDate}
        agents={agencyUsers
          .filter((au: AgencyUserWithAgency) => selectedUserIds.has(au.userId))
          .map((au: AgencyUserWithAgency) => ({ userId: au.userId, name: au.user?.name || au.user?.email || "Agent", agencyName: (au as any).agency?.name ?? (au as any).agencyName ?? null, avatarUrl: (au.user as any)?.profileImageUrl || (au.user as any)?.profile_image_url || null }))}
      />
      <div className="flex justify-between items-center mb-4">
        <CardTitle className="flex gap-2">
          Agents ({agencyUsers.length}{pendingInvites.length > 0 ? ` + ${pendingInvites.length} pending` : ''})
        </CardTitle>
        <div data-tour="invite-agents" className="flex gap-2 justify-end">
          {selectedUserIds.size > 1 && (
            <Button size="sm" variant="outline" onClick={() => setCompareOpen(true)} title="Compare the selected agents side by side for the selected date range">
              <Users className="w-4 h-4 mr-2" />
              Compare {selectedUserIds.size}
            </Button>
          )}
          {selectedUserIds.size > 0 && (
            <Button size="sm" onClick={() => setShowBulkMove(true)}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Move {selectedUserIds.size} Agent{selectedUserIds.size === 1 ? "" : "s"}
            </Button>
          )}
          <InviteUsersModal
            agency={{ id: agencyId, name: "Current Agency", description: "", contactEmail: "", isActive: true, updatedAt: "", createdAt: "", deletedAt: null }}
            trigger={<Button size="sm" variant="outline">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Agent
            </Button>}
          />
        </div>
      </div>

      <BulkMoveAgentsModal
        open={showBulkMove}
        onClose={() => setShowBulkMove(false)}
        sourceAgencyId={agencyId}
        sourceAgencyName="this agency"
        selectedUserIds={Array.from(selectedUserIds)}
        onSuccess={() => setSelectedUserIds(new Set())}
      />

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users by name, email, role, title, or account type..."
            value={searchTerm}
          />
        </div>
        {!!searchTerm && (
          <div className="mt-2 text-sm text-muted-foreground">
            Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}{filteredPendingInvites.length > 0 ? ` and ${filteredPendingInvites.length} pending invite${filteredPendingInvites.length !== 1 ? 's' : ''}` : ''} matching &quot;{searchTerm}&quot;
          </div>
        )}
      </div>

      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  checked={paginatedUsers.length > 0 && paginatedUsers.every((au: AgencyUserWithAgency) => selectedUserIds.has(au.userId))}
                  onChange={(e) =>
                    setSelectedUserIds((prev) => {
                      const next = new Set(prev);
                      paginatedUsers.forEach((au: AgencyUserWithAgency) =>
                        e.target.checked ? next.add(au.userId) : next.delete(au.userId),
                      );
                      return next;
                    })
                  }
                />
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Name</TableHead>
              {includeSubAgencies && <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Agency</TableHead>}
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Email</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Role</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Upline</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Title</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Account Type</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">A2P</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Status</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">First Login</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">Last Active</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedUsers.map((agencyUser: AgencyUserWithAgency) => {
              return (
                <TableRow
                  key={agencyUser.id}
                  className={`transition-colors ${onViewAgentDetails ? "cursor-pointer" : ""} ${
                    lockStateOf(agencyUser.user).kind === "disabled"
                      ? "bg-red-50/70 hover:bg-red-50"
                      : lockStateOf(agencyUser.user).kind === "suspended"
                        ? "bg-amber-50/70 hover:bg-amber-50"
                        : "hover:bg-gray-50/60"
                  }`}
                  onClick={() => onViewAgentDetails?.(agencyUser)}
                >
                  <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${agencyUser.user.name}`}
                      checked={selectedUserIds.has(agencyUser.userId)}
                      onChange={(e) =>
                        setSelectedUserIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(agencyUser.userId);
                          else next.delete(agencyUser.userId);
                          return next;
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-primary/20 shadow-sm">
                        <AvatarImage
                          src={(agencyUser.user as any).profileImageUrl || (agencyUser.user as any).profile_image_url || undefined}
                          alt={agencyUser.user.name}
                          className="object-cover"
                        />
                      <AvatarFallback className="text-sm bg-primary text-primary-foreground font-semibold">
                        {getInitialsFromFullName(
                          agencyUser.user.name?.split(" ")[0] || "U",
                          agencyUser.user.name?.split(" ")[1] || "N"
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-gray-900 whitespace-nowrap">{agencyUser.user.name}</span>
                    {(() => {
                      const st = lockStateOf(agencyUser.user);
                      if (st.kind === "active") return null;
                      return (
                        <span
                          className={`ml-1.5 ${pill(st.kind === "disabled" ? "red" : "amber")}`}
                          title={
                            st.kind === "disabled"
                              ? `Signed out, subscription cancelled and numbers released.${st.daysLeft !== null ? ` Data is deleted in ${st.daysLeft} day${st.daysLeft === 1 ? "" : "s"} unless restored.` : ""}`
                              : "Cannot sign in. Subscription, numbers and leads all kept."
                          }
                        >
                          {st.kind === "disabled" ? "Disabled" : "Suspended"}
                          {st.daysLeft !== null && ` · ${st.daysLeft}d left`}
                        </span>
                      );
                    })()}
                  </div>
                </TableCell>
                {includeSubAgencies && (
                  <TableCell>
                    <span className={pill('slate')}>
                      {(agencyUser as any).agency?.name || "—"}
                    </span>
                  </TableCell>
                )}
                <TableCell className="text-muted-foreground">
                  {agencyUser.user.email}
                </TableCell>
                <TableCell>
                  <span
                    className={pill(
                      agencyUser.role.toLowerCase() === 'owner'
                        ? 'red'
                        : ['manager', 'admin'].includes(agencyUser.role.toLowerCase())
                        ? 'violet'
                        : 'gray'
                    )}
                  >
                    {formatRole(agencyUser.role)}
                  </span>
                </TableCell>
                {/* Direct upline — derived server-side from the hierarchy, never
                    stored. Agents see their agency's owner; owners see whoever
                    outranks them. Hidden upline owners are already filtered out
                    of this list, and the same rule keeps them out of here. */}
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {(agencyUser as any).upline?.length ? (
                    (agencyUser as any).upline.map((u: any) => u.name).join(", ")
                  ) : ["owner", "agency_owner"].includes(String(agencyUser.role).toLowerCase()) ? (
                    // Top of the hierarchy — nobody above them. A dash here read as
                    // "we don't know", which is what Moe Ray flagged on his own row.
                    <span className="text-xs italic text-gray-400">Top of hierarchy</span>
                  ) : null}
                </TableCell>
                <TableCell>
                  {agencyUser.user.title ? (
                    <span className="text-sm text-gray-600 whitespace-nowrap">{agencyUser.user.title}</span>
                  ) : null}
                </TableCell>
                <TableCell>
                  {(() => {
                    const accountTypeInfo = getAccountTypeInfo(agencyUser.user.accountType);
                    return <span className={`${pill(accountTypeInfo.tone)} capitalize`}>{accountTypeInfo.label}</span>;
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const u = agencyUser.user as any;
                    if (u.a2pLocked) {
                      return <span className={pill('red')} title={u.a2pLockoutReason || 'A2P permanently locked'}>Locked</span>;
                    }
                    const status = u.a2pStatus || 'not_submitted';
                    if (status === 'verified') {
                      return <span className={pill('emerald')}><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Verified</span>;
                    }
                    if (status === 'pending_review') {
                      return <span className={pill('amber')} title={u.a2pSubmittedToTwilio ? 'Submitted to the carrier' : 'Awaiting admin submit'}>{u.a2pSubmittedToTwilio ? 'Submitted' : 'Pending'}</span>;
                    }
                    if (status === 'rejected') {
                      return <span className={pill('rose')}>Rejected</span>;
                    }
                    return <span className="text-xs text-gray-400 whitespace-nowrap">Not started</span>;
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const status = agencyUser.user.userStatus || (agencyUser.user.isOnline ? 'available' : 'offline');
                    const statusConfig: Record<string, { dot: string; tone: string; label: string }> = {
                      available: { dot: 'bg-green-500 animate-pulse', tone: 'green', label: 'Online' },
                      idle: { dot: 'bg-yellow-500', tone: 'yellow', label: 'Idle' },
                      on_call: { dot: 'bg-blue-500 animate-pulse', tone: 'blue', label: 'On Call' },
                      dnd: { dot: 'bg-red-500', tone: 'red', label: 'Do Not Disturb' },
                      offline: { dot: 'bg-gray-300', tone: 'gray', label: 'Offline' },
                    };
                    const config = statusConfig[status] || statusConfig.offline;
                    return (
                      <span className={pill(config.tone as any)}>
                        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
                        {config.label}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const firstLogin = (agencyUser.user as any)?.firstLoginToday;
                    if (!firstLogin) return <span className="text-xs text-muted-foreground">—</span>;
                    const loginDate = new Date(firstLogin);
                    return (
                      <span className="text-xs font-medium text-foreground">
                        {loginDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const lastActive = (agencyUser.user as any)?.lastActiveAt;
                    if (!lastActive) return <span className="text-xs text-muted-foreground">—</span>;
                    const activeDate = new Date(lastActive);
                    const now = new Date();
                    const diffMs = now.getTime() - activeDate.getTime();
                    // Future timestamps (bad/seeded data) must not read as "Just now"
                    if (diffMs < 0) return <span className="text-xs text-muted-foreground">{activeDate.toLocaleDateString()}</span>;
                    const diffMin = Math.floor(diffMs / 60000);
                    if (diffMin < 1) return <span className="text-xs font-medium text-green-600">Just now</span>;
                    if (diffMin < 60) return <span className="text-xs text-muted-foreground">{diffMin}m ago</span>;
                    const diffHrs = Math.floor(diffMin / 60);
                    if (diffHrs < 24) return <span className="text-xs text-muted-foreground">{diffHrs}h ago</span>;
                    return <span className="text-xs text-muted-foreground">{activeDate.toLocaleDateString()}</span>;
                  })()}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 items-center justify-center" data-tour="agent-actions">
                    {onViewAgentDetails && (
                      <Button
                        className="h-8 w-8 p-0 cursor-pointer"
                        onClick={() => onViewAgentDetails(agencyUser)}
                        size="sm"
                        title="View Performance Details"
                        variant="ghost"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                    )}
                    {agencyUser.userId !== currentUserId && (
                    <EditUserRoleModal
                      agencyId={agencyId}
                      agencyUser={agencyUser}
                      trigger={
                        <Button
                          className="h-8 w-8 p-0 cursor-pointer"
                          size="sm"
                          variant="outline"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      }
                    />
                    )}
                    {agencyUser.userId !== currentUserId && (
                    <>
                    <MoveAgentModal
                      agencyUser={agencyUser}
                      // Use the agent's OWN (attributed) agency for BOTH id and
                      // name so the modal's "move from" source matches what's
                      // shown — previously id was the table root but the name was
                      // the per-user agency, which disagreed for multi-agency users.
                      currentAgencyId={agencyUser.agency?.id || agencyId}
                      currentAgencyName={agencyUser.agency?.name || "Current Agency"}
                      trigger={
                        <Button
                          className="h-8 w-8 p-0 cursor-pointer"
                          size="sm"
                          variant="outline"
                          title="Move to sub-agency"
                        >
                          <ArrowRightLeft className="h-3 w-3" />
                        </Button>
                      }
                    />
                    {/* Undo — only on a locked row. Lifts a suspension, or restores a
                        disabled account (which also stops the 30-day clock). */}
                    {lockStateOf(agencyUser.user).kind !== "active" && (
                      <Button
                        className="h-8 w-8 cursor-pointer p-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={(e) => { e.stopPropagation(); void handleUnlock(agencyUser); }}
                        size="sm"
                        variant="outline"
                        disabled={unsuspendMutation.isPending || restoreMutation.isPending}
                        title={
                          lockStateOf(agencyUser.user).kind === "disabled"
                            ? "Restore this account — they can sign in again and the deletion clock stops"
                            : "Lift the suspension — they can sign in again"
                        }
                      >
                        <LockOpen className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      className="h-8 w-8 p-0 cursor-pointer"
                      onClick={() => handleDeleteUser(agencyUser)}
                      size="sm"
                      variant="destructive"
                    >
                      <Trash2 className="h-3 w-3 text-white" />
                    </Button>
                    </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
            })}
            {/* Pending Invites */}
            {filteredPendingInvites.map((invite: PendingInvite) => (
              <TableRow key={`invite-${invite.id}`} className="bg-yellow-50/50">
                <TableCell className="font-medium">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 border border-dashed border-yellow-400 bg-yellow-100">
                      <AvatarFallback className="text-sm bg-yellow-100 text-yellow-600">
                        <Mail className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-muted-foreground italic">Pending Invitation</span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {invite.email}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className="bg-white text-black border-gray-300 hover:bg-gray-50"
                  >
                    {formatRole(invite.role)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  -
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className="bg-white text-black border-gray-300 hover:bg-gray-50"
                  >
                    -
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <Badge
                      variant="outline"
                      className="bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-100"
                    >
                      Pending
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 items-center">
                    <Button
                      className="h-8 w-8 p-0 cursor-pointer"
                      onClick={() => handleCancelInvite(invite)}
                      size="sm"
                      variant="destructive"
                      title="Cancel Invite"
                      disabled={cancelInviteMutation.isPending}
                    >
                      <X className="h-3 w-3 text-white" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Pagination Controls */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">Per page</span>
                <select
                  className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  aria-label="Rows per page"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                disabled={!hasPrevPage}
                onClick={handlePrevPage}
                size="sm"
                variant="outline"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    className="w-8 h-8 p-0"
                    onClick={() => handlePageChange(page)}
                    size="sm"
                    variant={currentPage === page ? "default" : "outline"}
                  >
                    {page}
                  </Button>
                ))}
              </div>
              
              <Button
                disabled={!hasNextPage}
                onClick={handleNextPage}
                size="sm"
                variant="outline"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
      <RemoveAgentDialog
        open={showRemoveChoice}
        onOpenChange={(open) => {
          setShowRemoveChoice(open);
          if (!open && !showMFAModal) setUserToDelete(null);
        }}
        agentName={userToDelete?.user.name || "this user"}
        eligibility={eligibility}
        onChoose={handleRemoveChoice}
      />
      {/* MFA Verification Modal for Remove / Disable User */}
      <SimplifiedMFAModal
        isOpen={showMFAModal}
        onClose={() => {
          setShowMFAModal(false);
          setUserToDelete(null);
        }}
        onVerified={handleMFAVerified}
        onVerify={handleMFAVerify}
        onSendSMS={resendSMSForAction}
        title={
          removeMode === "disable"
            ? "Verify to Disable Account"
            : removeMode === "suspend"
              ? "Verify to Suspend Agent"
              : "Verify to Remove User"
        }
        description={
          removeMode === "disable"
            ? `Please verify your identity with 2FA to disable ${userToDelete?.user.name || 'this user'}'s account. They will be signed out, their subscription and numbers cancelled without refund.`
            : removeMode === "suspend"
              ? `Please verify your identity with 2FA to suspend ${userToDelete?.user.name || 'this user'}. They will be signed out and unable to sign back in. Their subscription, numbers and leads are all kept.`
              : `Please verify your identity with 2FA to remove ${userToDelete?.user.name || 'this user'} from the agency. They will become an independent agent.`
        }
      />
    </Card>
  );
};
